import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { uploadsApi, ApiError, type Upload } from "../lib/api";
import UploadForm from "../components/UploadForm";
import UploadDetailView from "../components/UploadDetailView";
import Modal from "../components/Modal";

const POLL_INTERVAL_MS = 5000;

/**
 * /data — trade log uploads.
 *
 * List view by default. Click a row to drill into the detail view (stats +
 * narrative + parsed trades). "Upload" button opens the modal.
 *
 * If any upload in the list is in a non-terminal status (pending/parsing),
 * we poll every 5s until all uploads are in a terminal status.
 */
export default function Data() {
  const [uploads, setUploads] = useState<Upload[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const pollingRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const items = await uploadsApi.list();
      setUploads(items);
    } catch (e) {
      setError(messageFrom(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Poll while any upload is in a transient state.
  useEffect(() => {
    const inFlight = (uploads ?? []).some(
      (u) => u.status === "pending" || u.status === "parsing",
    );
    if (!inFlight) {
      if (pollingRef.current !== null) {
        window.clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }
    if (pollingRef.current === null) {
      pollingRef.current = window.setInterval(() => {
        void refresh();
      }, POLL_INTERVAL_MS);
    }
    return () => {
      if (pollingRef.current !== null) {
        window.clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [uploads, refresh]);

  const sorted = useMemo(() => {
    if (!uploads) return null;
    return [...uploads].sort((a, b) => b.uploaded_at.localeCompare(a.uploaded_at));
  }, [uploads]);

  if (selected) {
    return (
      <UploadDetailView
        upload_id={selected}
        onClose={() => {
          setSelected(null);
          void refresh();
        }}
      />
    );
  }

  return (
    <section className="accounts">
      <header className="accounts-header">
        <div>
          <h1>Data</h1>
          <div className="muted">Trade log uploads — newest first.</div>
        </div>
        <div className="header-actions">
          <button className="link-button" onClick={() => void refresh()}>
            Refresh
          </button>
          <button className="primary" onClick={() => setUploadOpen(true)}>
            + Upload trade log
          </button>
        </div>
      </header>

      {error && <div className="error">{error}</div>}

      {sorted === null ? (
        <p>Loading…</p>
      ) : sorted.length === 0 ? (
        <p className="empty">
          No uploads yet. Click <strong>+ Upload trade log</strong> to add one.
        </p>
      ) : (
        <table className="accounts-table">
          <thead>
            <tr>
              <th>Uploaded</th>
              <th>Filename</th>
              <th>Firm</th>
              <th>Size</th>
              <th>Status</th>
              <th>Trades</th>
              <th>Gross P&L</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((u) => (
              <tr key={u.upload_id}>
                <td className="mono">{new Date(u.uploaded_at).toLocaleString()}</td>
                <td>{u.filename}</td>
                <td>{u.firm_name}</td>
                <td className="num">{(u.file_size / 1024).toFixed(0)} KB</td>
                <td>
                  <span className={`status-badge status-${u.status}`}>
                    {u.status.replace("_", " ")}
                  </span>
                </td>
                <td className="num">
                  {u.trade_count != null ? u.trade_count : <span className="muted">—</span>}
                </td>
                <td
                  className={
                    "num " +
                    (u.stats == null
                      ? ""
                      : u.stats.gross_pnl >= 0
                        ? "pnl-pos"
                        : "pnl-neg")
                  }
                >
                  {u.stats
                    ? `${u.stats.gross_pnl >= 0 ? "+" : ""}$${u.stats.gross_pnl.toFixed(2)}`
                    : "—"}
                </td>
                <td className="row-actions">
                  <button
                    className="link-button"
                    onClick={() => setSelected(u.upload_id)}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal open={uploadOpen} onClose={() => setUploadOpen(false)}>
        <UploadForm
          onCancel={() => setUploadOpen(false)}
          onUploaded={() => {
            setUploadOpen(false);
            void refresh();
          }}
        />
      </Modal>
    </section>
  );
}

function messageFrom(e: unknown): string {
  if (e instanceof ApiError) return `${e.message} (HTTP ${e.status})`;
  if (e instanceof Error) return e.message;
  return String(e);
}
